import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Megaphone, Camera, MessageCircle, Mail, Instagram, Phone, Globe, Facebook, ChevronRight,
  Vote, BarChart3, Loader2, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { CIDADAO } from "@/constants/testIds";
import { ensureClientId } from "@/components/AudienceHeartbeat";

const KIND_ICONS = {
  whatsapp: MessageCircle,
  email: Mail,
  instagram: Instagram,
  facebook: Facebook,
  phone: Phone,
  url: Globe,
  other: Globe,
};

const KIND_COLORS = {
  whatsapp: "text-emerald-500",
  email: "text-[var(--fi-red)]",
  instagram: "text-[var(--fi-grad-2)]",
  facebook: "text-blue-600",
  phone: "text-[var(--fi-blue)]",
  url: "text-[var(--fi-blue)]",
  other: "text-gray-500",
};

const KIND_LABEL = {
  whatsapp: "WhatsApp",
  email: "E-mail",
  instagram: "Instagram",
  facebook: "Facebook",
  phone: "Telefone",
  url: "Site",
  other: "Contato",
};

const buildHref = (c) => {
  switch (c.kind) {
    case "whatsapp":
      return `https://wa.me/${c.value.replace(/\D/g, "")}`;
    case "email":
      return `mailto:${c.value}`;
    case "phone":
      return `tel:${c.value.replace(/[^\d+]/g, "")}`;
    case "instagram": {
      const handle = c.value.replace(/^@/, "");
      return `https://instagram.com/${handle}`;
    }
    case "facebook": {
      const v = c.value.trim();
      return v.startsWith("http") ? v : `https://facebook.com/${v.replace(/^@/, "")}`;
    }
    case "url":
    case "other":
    default:
      return c.value.startsWith("http") ? c.value : `https://${c.value}`;
  }
};

const buildDisplay = (c) => {
  switch (c.kind) {
    case "whatsapp": {
      const digits = c.value.replace(/\D/g, "");
      if (digits.length >= 12) {
        const cc = digits.slice(0, digits.length - 11);
        const ddd = digits.slice(-11, -9);
        const first = digits.slice(-9, -4);
        const last = digits.slice(-4);
        return `+${cc} (${ddd}) ${first}-${last}`;
      }
      return c.value;
    }
    case "instagram":
      return "@" + c.value.replace(/^@/, "");
    default:
      return c.value;
  }
};

const PollCard = ({ poll, onVoted }) => {
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [voted, setVoted] = useState(null);
  const [state, setState] = useState(poll);
  const clientId = ensureClientId();

  useEffect(() => {
    api.get(`/polls/${poll.id}/has-voted`, { params: { client_id: clientId } })
      .then((r) => setVoted(r.data.option_id))
      .catch(() => {});
  }, [poll.id, clientId]);

  useEffect(() => setState(poll), [poll]);

  const submit = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/polls/${poll.id}/vote`, { option_id: selected, client_id: clientId });
      setState(data);
      setVoted(selected);
      toast.success("Voto registrado! Obrigado.");
      onVoted?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const hasVoted = voted !== null && voted !== undefined;
  const showResults = hasVoted;

  return (
    <div
      data-testid={CIDADAO.pollCard(poll.id)}
      className="rounded-xl bg-white p-5 shadow-[0_6px_20px_rgba(13,27,75,0.05)]"
    >
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold text-[var(--fi-red)]">
        <Vote className="w-3.5 h-3.5" /> Enquete
      </div>
      <div className="font-display text-lg sm:text-xl font-extrabold text-[var(--fi-blue)] leading-tight mt-1">
        {state.question}
      </div>

      <div className="mt-4 space-y-2">
        {state.options.map((o) => {
          const pct = state.total_votes > 0 ? (o.votes / state.total_votes) * 100 : 0;
          const isSelected = selected === o.id;
          const isUserPick = voted === o.id;
          if (showResults) {
            return (
              <div key={o.id} className={`p-2 rounded-lg border ${isUserPick ? "border-[var(--fi-red)] bg-red-50" : "border-gray-100"}`}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className={`font-semibold ${isUserPick ? "text-[var(--fi-red)]" : "text-[var(--fi-blue)]"}`}>
                    {isUserPick && <CheckCircle2 className="inline w-4 h-4 mr-1 -mt-0.5" />}
                    {o.text}
                  </span>
                  <span className="text-xs text-gray-500 font-bold">{pct.toFixed(1)}%</span>
                </div>
                <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                  <div className={`h-full ${isUserPick ? "bg-[var(--fi-red)]" : "fi-gradient"}`} style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          }
          return (
            <button
              key={o.id}
              data-testid={CIDADAO.pollOption(poll.id, o.id)}
              onClick={() => setSelected(o.id)}
              className={`w-full text-left px-4 py-3 rounded-xl border text-sm font-semibold transition-colors ${
                isSelected
                  ? "border-[var(--fi-blue)] bg-[var(--fi-blue)]/5 text-[var(--fi-blue)]"
                  : "border-gray-200 text-gray-700 hover:border-[var(--fi-blue)] hover:text-[var(--fi-blue)]"
              }`}
            >
              {o.text}
            </button>
          );
        })}
      </div>

      <div className="mt-4 flex items-center justify-between">
        <div className="text-xs text-gray-500 inline-flex items-center gap-1">
          <BarChart3 className="w-3.5 h-3.5" />
          {state.total_votes} voto(s)
        </div>
        {!hasVoted && (
          <button
            data-testid={CIDADAO.pollVote(poll.id)}
            disabled={busy || !selected}
            onClick={submit}
            className="inline-flex items-center gap-2 rounded-full bg-[var(--fi-red)] hover:bg-[var(--fi-red-hover)] text-white font-bold uppercase tracking-widest text-xs px-4 py-2 transition-colors disabled:opacity-40"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Votar"}
          </button>
        )}
      </div>
    </div>
  );
};

const CidadaoPanel = () => {
  const [polls, setPolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [contacts, setContacts] = useState([]);

  const load = async () => {
    try {
      const { data } = await api.get("/polls/active");
      setPolls(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    api.get("/settings/contacts").then((r) => setContacts(r.data)).catch(() => {});
    return () => clearInterval(t);
  }, []);

  return (
    <section className="max-w-6xl mx-auto px-4 md:px-6 pt-6 md:pt-10">
      <div className="mb-2 text-xs uppercase tracking-[0.28em] text-[var(--fi-red)] font-bold">
        Sua voz importa
      </div>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black uppercase text-[var(--fi-blue)] leading-[0.9] mb-2">
        Painel do Cidadão
      </h1>
      <p className="text-gray-600 max-w-2xl">
        Participe: envie sua reclamação, uma pauta para a redação ou vote nas enquetes da semana.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-8">
        <Link
          to="/contato/reclamacao"
          data-testid={CIDADAO.cardComplaint}
          className="group relative overflow-hidden rounded-2xl p-6 sm:p-8 text-white shadow-[0_12px_40px_rgba(13,27,75,0.18)] hover:scale-[1.01] transition-transform"
          style={{ background: "linear-gradient(135deg, #0D1B4B 0%, #1a2960 55%, #0D1B4B 100%)" }}
        >
          <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full fi-gradient opacity-25 blur-3xl" />
          <Megaphone className="w-10 h-10 mb-6 text-[var(--fi-red)]" strokeWidth={2.2} />
          <div className="text-[11px] uppercase tracking-[0.28em] font-bold text-white/70">Formulário</div>
          <div className="font-display text-3xl font-extrabold uppercase leading-tight mt-1">Enviar Reclamação</div>
          <p className="text-sm text-white/80 mt-2 max-w-xs">Buraco na rua, iluminação, coleta de lixo… nós ouvimos.</p>
          <div className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-white">
            Abrir <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </div>
        </Link>

        <Link
          to="/contato/reportagem"
          data-testid={CIDADAO.cardReport}
          className="group relative overflow-hidden rounded-2xl p-6 sm:p-8 text-white shadow-[0_12px_40px_rgba(215,38,56,0.35)] hover:scale-[1.01] transition-transform"
          style={{ background: "linear-gradient(135deg, #d72638 0%, #b81f2e 55%, #d72638 100%)" }}
        >
          <div className="absolute -bottom-24 -left-16 w-64 h-64 rounded-full fi-gradient opacity-25 blur-3xl" />
          <Camera className="w-10 h-10 mb-6 text-white" strokeWidth={2.2} />
          <div className="text-[11px] uppercase tracking-[0.28em] font-bold text-white/80">Formulário</div>
          <div className="font-display text-3xl font-extrabold uppercase leading-tight mt-1">Chamar Reportagem</div>
          <p className="text-sm text-white/90 mt-2 max-w-xs">Viu algo importante? Seja nossa fonte na região.</p>
          <div className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-white">
            Abrir <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </div>
        </Link>
      </div>

      <div className="mt-10" data-testid={CIDADAO.pollsSection}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs uppercase tracking-[0.28em] text-[var(--fi-red)] font-bold">Ao vivo</div>
            <h2 className="font-display text-3xl font-black uppercase text-[var(--fi-blue)] leading-tight">Enquetes da semana</h2>
          </div>
          <div className="text-xs uppercase tracking-widest font-bold text-gray-500 inline-flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-emerald-500 live-dot" />
            Tempo real
          </div>
        </div>
        {loading ? (
          <div className="text-center py-10 text-gray-400"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
        ) : polls.length === 0 ? (
          <div className="rounded-xl bg-white p-10 text-center text-gray-400 shadow-[0_6px_20px_rgba(13,27,75,0.05)]">
            Nenhuma enquete ativa no momento. Volte em breve!
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {polls.map((p) => <PollCard key={p.id} poll={p} onVoted={load} />)}
          </div>
        )}
      </div>

      {contacts.length > 0 && (
        <div className="mt-10 rounded-xl bg-white p-5 shadow-[0_6px_24px_rgba(13,27,75,0.06)]">
          <div className="text-xs uppercase tracking-[0.28em] text-[var(--fi-blue)]/60 font-bold mb-3">
            Contato direto
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {contacts.map((c) => {
              const Icon = KIND_ICONS[c.kind] || Globe;
              return (
                <a
                  key={c.id}
                  data-testid={`contact-item-${c.id}`}
                  href={buildHref(c)}
                  target={c.kind === "email" || c.kind === "phone" ? undefined : "_blank"}
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 rounded-xl border border-gray-200 hover:border-[var(--fi-blue)] p-4 transition-colors"
                >
                  <Icon className={`w-6 h-6 ${KIND_COLORS[c.kind] || "text-gray-500"}`} />
                  <div className="min-w-0">
                    <div className="text-xs uppercase tracking-widest font-bold text-gray-500 truncate">
                      {c.label || KIND_LABEL[c.kind] || "Contato"}
                    </div>
                    <div className="text-sm font-bold text-[var(--fi-blue)] truncate">{buildDisplay(c)}</div>
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
};

export default CidadaoPanel;
